export type CalendlyPrefill = {
  name?: string
  email?: string
  customAnswers?: { a1?: string }
}

type CalendlyInitProps = {
  url: string
  parentElement: HTMLElement
  prefill?: CalendlyPrefill
  utm?: {
    utmCampaign?: string
    utmSource?: string
    utmMedium?: string
    utmContent?: string
  }
  /** Grow/shrink the embed to content height (Calendly posts calendly.page_height). */
  resize?: boolean
}

type CalendlyApi = {
  initInlineWidget: (props: CalendlyInitProps) => void
}

/** Read widget API without redeclaring Window.Calendly (see calendly_controller). */
function getCalendlyApi(): CalendlyApi | undefined {
  return (window as unknown as { Calendly?: CalendlyApi }).Calendly
}

const WIDGET_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js'

export function isCalendlyMessageEvent(
  event: MessageEvent,
): event is MessageEvent<{ event: string; payload?: { height?: string } }> {
  return (
    event.origin === 'https://calendly.com' &&
    typeof event.data === 'object' &&
    event.data != null &&
    typeof (event.data as { event?: unknown }).event === 'string' &&
    String((event.data as { event: string }).event).indexOf('calendly.') === 0
  )
}

/** Parse calendly.page_height payload (e.g. "752px") into pixels. */
export function calendlyPageHeightPx(
  event: MessageEvent<{ event: string; payload?: { height?: string } }>,
): number | null {
  if (event.data.event !== 'calendly.page_height') return null
  const raw = event.data.payload?.height
  if (!raw) return null
  const px = Number.parseInt(String(raw).replace(/px$/i, ''))
  return Number.isFinite(px) && px > 0 ? px : null
}

export function loadCalendlyWidgetScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (getCalendlyApi()) return Promise.resolve()

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${WIDGET_SCRIPT}"]`,
  )
  if (existing) {
    return new Promise((resolve, reject) => {
      if (getCalendlyApi()) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Calendly script failed')), {
        once: true,
      })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = WIDGET_SCRIPT
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Calendly script failed'))
    document.head.appendChild(script)
  })
}

export function mountCalendlyInlineWidget(
  parent: HTMLElement,
  options: Omit<CalendlyInitProps, 'parentElement'>,
): void {
  parent.innerHTML = ''
  getCalendlyApi()?.initInlineWidget({
    ...options,
    parentElement: parent,
    resize: options.resize ?? true,
  })
}
